import { useState } from 'react';
import { useDomains, useLogs } from '@/lib/queries';
import LogList from '@/components/LogList';
import LogForm from '@/components/LogForm';
import { Button } from '@/components/ui/button';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

/**
 * Global maintenance log index. Filter pills on top; card-list of
 * the latest 100 entries below. New-log entry point is the "+ New
 * log" button up top + the global Capture sheet.
 */
export default function Logs() {
  const [domainFilter, setDomainFilter] = useState<string>('');
  const logs = useLogs({
    domain: domainFilter || undefined,
    limit: 100,
  });
  const domains = useDomains();
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['logs'] });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-8">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Operations · Maintenance log
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Logs
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              The longitudinal memory of the property — what got done, when, with what, by whom.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} className="h-11 self-start sm:self-auto" data-testid="new-log">
            <Plus className="h-4 w-4 mr-2" aria-hidden /> New log
          </Button>
        </header>

        <div className="flex items-center gap-2 flex-wrap">
          <FilterPill
            label="All domains"
            active={domainFilter === ''}
            onClick={() => setDomainFilter('')}
          />
          {(domains.data ?? []).map((d) => (
            <FilterPill
              key={d.id}
              label={d.name}
              active={domainFilter === d.slug}
              onClick={() => setDomainFilter(d.slug)}
            />
          ))}
        </div>

        {logs.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {logs.data ? (
          <LogList logs={logs.data} emptyMessage="No logs yet — tap + New log to capture one." />
        ) : null}

        <LogForm open={creating} onOpenChange={setCreating} mode="create" />
      </div>
    </PullToRefresh>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ' +
        (active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-rule text-muted-foreground hover:border-primary/30 hover:text-foreground')
      }
    >
      {label}
    </button>
  );
}
